import { inject, Injectable } from '@angular/core';
import { MatDialog, MatDialogConfig, MatDialogRef } from '@angular/material/dialog';
import { firstValueFrom } from 'rxjs';
import { ComponentType } from '@angular/cdk/overlay';
import { MapSelectData, MapSelectDialogComponent } from '../company/manage/map-select-dialog.component';
import {
  AppId,
  Company,
  ChainDocument,
  CompanySite,
  DDSPreviewData,
  Document,
  DocumentType,
  HasId,
  HasIdName,
  ProductChain,
  ReportArchive,
  TracesProduct,
  WhispSiteView,
} from '../model';
import { ConfirmDeleteDialogComponent } from './confirm-delete-dialog.component';
import { DocumentUploadDialogComponent, DocumentUploadMode } from './document/document-upload-dialog.component';
import { NewsDialogComponent } from '../company/evaluation/news-dialog.component';
import { DocumentsDialogComponent } from '../company/evaluation/documents-dialog.component';
import { ExploreLandDialogComponent } from '../company/evaluation/explore-land-dialog.component';
import { DocumentEditDetailDialogComponent, RowData } from '../company/document/document-edit-detail-dialog.component';
import { PDFPreviewDialogComponent } from '../company/evaluation/pdf-preview-dialog.component';
import { ConfirmDialogComponent } from './confirm-dialog.component';
import { TracesDdsConfirmRetractDialogComponent } from '../traces/traces-dds-confirm-retract-dialog.component';
import { MapViewDialogComponent } from './map-view-dialog.component';
import { TracesDdsReportSelectorDialogComponent } from '../traces/traces-dds-report-selector-dialog.component';
import { DDSRecord, ProductForm } from '../traces/traces-dds-detail.component';
import { TracesProductDdsDialogComponent } from '../traces/traces-product-dds-dialog.component';
import { BreakpointObserver } from '@angular/cdk/layout';
import {
  DdsCreationResult,
  TracesDdsConfirmSubmitDialogComponent,
} from '../traces/traces-dds-confirm-submit-dialog.component';
import { DocumentLinkDialogComponent } from '../document-manager/document-link-dialog.component';
import { clearDocumentLinkSidebarState } from '../document-manager/document-link-context.util';

@Injectable({ providedIn: 'root' })
export class Dialog {
  private dialog = inject(MatDialog);
  private bo = inject(BreakpointObserver);
  isMobile_Tablet = this.bo.isMatched('(max-width: 1025px)'); //for mobile and tablets
  isMobile_only = this.bo.isMatched('(max-width: 599px)'); //for mobile only
  isNarrowScreen = this.bo.isMatched('(max-width: 729px)');
  private documentLinkDialogRef: MatDialogRef<DocumentLinkDialogComponent> | null = null;

  open = async <R = any>(component: ComponentType<any>, config?: MatDialogConfig): Promise<R> =>
    await firstValueFrom<R>(this.dialog.open(component, config).afterClosed());

  uploadDocument = async <R = Document>(
    title: string,
    types: DocumentType[],
    file?: File,
    mode?: DocumentUploadMode<R>
  ) =>
    this.open<R>(DocumentUploadDialogComponent, {
      autoFocus: true,
      disableClose: true,
      width: this.isMobile_only ? '90vw' : '100vw',
      maxWidth: '600px',
      data: { types, title, file, ...mode },
    });

  selectCompanyDocument = async (
    company: Company,
    site: CompanySite,
    options?: {
      category?: 'company' | 'chain' | 'report' | 'attributes';
      originTitle?: string;
      linkedDocumentId?: string | null;
      formStateKey?: string | null;
      linkedArticleId?: string | null;
      restorePreviewDocumentId?: string | null;
    }
  ) => {
    this.documentLinkDialogRef?.close();
    const dialogRef = this.dialog.open(DocumentLinkDialogComponent, {
      disableClose: true,
      width: this.isMobile_only ? '100vw' : '435px',
      maxWidth: '100vw',
      height: '100dvh',
      maxHeight: '100dvh',
      position: { right: '0', top: '0' },
      panelClass: 'document-link-sidebar-panel',
      data: {
        company,
        site,
        category: options?.category,
        originTitle: options?.originTitle,
        linkedDocumentId: options?.linkedDocumentId ?? null,
        formStateKey: options?.formStateKey ?? null,
        linkedArticleId: (options as any)?.linkedArticleId ?? null,
        restorePreviewDocumentId: options?.restorePreviewDocumentId ?? null,
      },
    });
    this.documentLinkDialogRef = dialogRef;
    dialogRef.afterClosed().subscribe(() => {
      if (this.documentLinkDialogRef === dialogRef) {
        this.documentLinkDialogRef = null;
      }
      clearDocumentLinkSidebarState();
    });
    return await firstValueFrom(dialogRef.afterClosed());
  };
  closeDocumentLinkDialog() {
    this.documentLinkDialogRef?.close();
    this.documentLinkDialogRef = null;
  }

  selectLocation = async (data: MapSelectData) =>
    this.open(MapSelectDialogComponent, {
      disableClose: true,
      closePredicate: (result, _config, component) =>
        (component as MapSelectDialogComponent | null)?.canCloseMap(result) ?? true,
      width: '100vw',
      height: '100vh',
      maxWidth: '100vw',
      panelClass: 'full-screen-dialog',
      data,
    });
  editDocumentDetail = async (document: Document) =>
    this.open<RowData[] | undefined>(DocumentEditDetailDialogComponent, {
      disableClose: true,
      width: this.isMobile_only ? '90vw' : '100vw',
      maxWidth: '95vw',
      autoFocus: false,
      data: { document },
    });

  confirmDelete = async (name: string, type: string) =>
    this.open(ConfirmDeleteDialogComponent, {
      disableClose: true,
      width: this.isMobile_only ? '90vw' : '100vw',
      maxWidth: '600px',
      data: { name, type },
    });

  confirm = async (title: string, message: string, button: string) =>
    this.open(ConfirmDialogComponent, {
      disableClose: true,
      width: this.isMobile_only ? '90vw' : '100vw',
      maxWidth: '600px',
      data: { title, message, button },
    });

  showNews = async (risks: HasId[], locations: HasIdName[]) =>
    this.open(NewsDialogComponent, {
      width: '100vw',
      maxWidth: 'calc(100vw - 30px)',
      height: 'calc(100vh - 50px)',
      data: { risks, locations },
    });

  showExploreLand = async (id: string) =>
    this.open(ExploreLandDialogComponent, {
      width: '100vw',
      maxWidth: 'calc(100vw - 30px)',
      height: 'calc(100vh - 50px)',
      data: { id },
    });

  confirmDDSCreation = async (previewData: DDSPreviewData) =>
    this.open<DdsCreationResult>(TracesDdsConfirmSubmitDialogComponent, {
      disableClose: true,
      width: this.isMobile_only ? '90vw' : '100vw',
      maxWidth: '800px',
      data: { previewData },
    });

  confirmDDSRetraction = async (product: TracesProduct) =>
    this.open<boolean>(TracesDdsConfirmRetractDialogComponent, {
      disableClose: true,
      width: this.isMobile_only ? '90vw' : '100vw',
      maxWidth: '600px',
      data: { product },
    });

  showProductDocuments = async (product: ProductChain) => this.showDocuments(product.documents);

  showDocuments = async (documents: ChainDocument[]) => {
    const isSmallViewport = typeof window !== 'undefined' && window.matchMedia('(max-width: 700px)').matches;
    return this.open(DocumentsDialogComponent, {
      width: isSmallViewport ? '96vw' : '100vw',
      maxWidth: isSmallViewport ? '96vw' : 'calc(60vw - 30px)',
      minHeight: isSmallViewport ? '60dvh' : 'calc(60vh - 50px)',
      data: { documents },
    });
  };

  showPdf = async (title: string, pdf: Blob, options: { fileName?: string; mimeType?: string } = {}) =>
    this.open(PDFPreviewDialogComponent, {
      autoFocus: false,
      width: '100vw',
      maxWidth: this.isMobile_only ? '100vw' : 'calc(100vw - 30px)',
      height: this.isMobile_only ? '100dvh' : 'calc(100vh - 50px)',
      data: { pdf, title, fileName: options.fileName, mimeType: options.mimeType },
    });

  viewGeoJsonMap = async (
    geoJson: string,
    opts?: {
      markers?: [number, number][];
      highlightPlaces?: number[];
      whisp?: WhispSiteView | null;
      fitBbox?: number[];
      whispGroupId?: number;
      tracesGeoJson?: string | null;
      siteId?: AppId;
    }
  ) =>
    this.open(MapViewDialogComponent, {
      disableClose: false,
      width: '100vw',
      maxWidth: this.isMobile_Tablet ? '100vw' : 'calc(100vw - 30px)',
      height: this.isMobile_Tablet ? '100dvh' : 'calc(100vh - 50px)',
      panelClass: 'full-screen-dialog',
      data: {
        geoJson,
        markers: opts?.markers,
        highlightPlaces: opts?.highlightPlaces,
        whisp: opts?.whisp,
        fitBbox: opts?.fitBbox,
        whispGroupId: opts?.whispGroupId,
        tracesGeoJson: opts?.tracesGeoJson,
        siteId: opts?.siteId,
      },
    });

  tracesReportSelector = async (product: ProductForm, site: CompanySite, reportCache: Map<string, ReportArchive>) =>
    this.open(TracesDdsReportSelectorDialogComponent, {
      data: { product, site, reportCache },
      height: '100%',
      maxHeight: this.isMobile_only ? '90vh' : '100vh',
      width: '100%',
      maxWidth: this.isMobile_only ? '100vw' : '1020px',
    });

  tracesDdsDialog = async (ddsData?: DDSRecord) =>
    this.open<DDSRecord>(TracesProductDdsDialogComponent, {
      data: { ddsData },
      height: '100%',
      maxHeight: this.isMobile_only ? '580px' : '580px',
      width: '100%',
      maxWidth: this.isMobile_only ? '400px' : '600px',
    });
}
